import paper from "paper";
import { getUnits } from "../preferences";

const MM_PER_INCH = 25.4;
/** On-screen size (CSS px) for the readout. The paper.js fontSize is in
 *  project-mm, so we divide by `view.zoom` to keep the rendered size stable
 *  regardless of the user's zoom level. */
const SCREEN_FONT_PX = 12;
/** Padding the text needs around it inside the shape before we decide it
 *  has to move outside (to the upper-left corner). Expressed as a fraction
 *  of the box dimension on each axis. */
const FIT_PADDING = 0.12;

export type BBox = { left: number; top: number; width: number; height: number };

/** Live "W × H mm/in" overlay shown while the user is drawing a rect /
 *  ellipse. Sits inside the shape when there's room; otherwise tucks
 *  itself just above the shape's top-left corner. Owned by the calling
 *  tool — the tool calls `update()` from its preview-refresh path and
 *  `remove()` when the drag ends or is cancelled.
 *
 *  `color` is the same hex string the calling tool uses for the dashed
 *  preview outline, so the readout reads as part of the same overlay
 *  rather than a separate UI element.
 *
 *  Optional `title` (passed via `update`) renders on a first line
 *  above the dimensions so callers can show e.g. "Construction -
 *  Square 1" / "2.0 × 2.0 in" together. Title is rendered as its own
 *  PointText item so paper.js's lack of multi-line layout doesn't
 *  matter — both items move together via the layout below. */
export class DimensionLabel {
  private item: paper.PointText | null = null;
  private titleItem: paper.PointText | null = null;
  constructor(private color: string) {}

  update(
    scope: paper.PaperScope,
    view: paper.View,
    box: BBox,
    title?: string,
  ): void {
    if (box.width <= 0 || box.height <= 0) {
      this.remove();
      return;
    }
    const units = getUnits();
    const text = formatDimensions(box.width, box.height, units);
    // Project-coordinate font size that renders at SCREEN_FONT_PX regardless
    // of the user's current zoom. paper.js redraws on every view change,
    // so a fresh fontSize here is enough.
    const fontSize = SCREEN_FONT_PX / view.zoom;

    if (!this.item) {
      this.item = new scope.PointText({
        content: text,
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize,
        fillColor: this.color,
        justification: "center",
      });
      this.item.data = { ephemeral: true };
    } else {
      this.item.content = text;
      this.item.fontSize = fontSize;
    }

    // Title item lifecycle — created lazily on first non-empty title,
    // removed when title is dropped, content / size kept in sync.
    if (title) {
      if (!this.titleItem) {
        this.titleItem = new scope.PointText({
          content: title,
          fontFamily: "system-ui, -apple-system, sans-serif",
          fontSize,
          fillColor: this.color,
          justification: "center",
        });
        this.titleItem.data = { ephemeral: true };
      } else {
        this.titleItem.content = title;
        this.titleItem.fontSize = fontSize;
      }
    } else if (this.titleItem) {
      this.titleItem.remove();
      this.titleItem = null;
    }

    // Layout: stack title above dimensions when both are present;
    // gap = ~25% of the font size so the two lines read as one block
    // rather than separate items. Combined bbox drives the fit-inside
    // vs corner-tuck decision.
    const dimBounds = this.item.bounds;
    const titleBounds = this.titleItem?.bounds;
    const lineGap = fontSize * 0.25;
    const combinedW = Math.max(
      dimBounds.width,
      titleBounds?.width ?? 0,
    );
    const combinedH =
      dimBounds.height + (titleBounds ? titleBounds.height + lineGap : 0);

    const padX = box.width * FIT_PADDING;
    const padY = box.height * FIT_PADDING;
    const fitsInside =
      combinedW + 2 * padX <= box.width && combinedH + 2 * padY <= box.height;
    const cx = box.left + box.width / 2;
    let topY: number;
    if (fitsInside) {
      topY = box.top + box.height / 2 - combinedH / 2;
    } else {
      const gap = 4 / view.zoom;
      topY = box.top - combinedH - gap;
    }
    if (this.titleItem) {
      // PointText positions its baseline center; bounds.center accounts
      // for that. Place the title's bounds so its top sits at topY.
      this.titleItem.position = new scope.Point(
        cx,
        topY + this.titleItem.bounds.height / 2,
      );
      this.item.position = new scope.Point(
        cx,
        topY +
          this.titleItem.bounds.height +
          lineGap +
          this.item.bounds.height / 2,
      );
    } else {
      this.item.position = new scope.Point(cx, topY + dimBounds.height / 2);
    }
  }

  remove(): void {
    if (this.item) {
      this.item.remove();
      this.item = null;
    }
    if (this.titleItem) {
      this.titleItem.remove();
      this.titleItem = null;
    }
  }
}

/** "30 × 40 mm" / "1.18 × 1.57 in" — uses U+00D7 (×) instead of plain "x"
 *  so the readout doesn't look like an algebraic expression. Trims trailing
 *  zeros so 30.0 mm renders as "30 mm" but 30.5 stays as "30.5 mm". */
function formatDimensions(
  widthMm: number,
  heightMm: number,
  units: "mm" | "in",
): string {
  if (units === "in") {
    return (
      `${trimTrailing((widthMm / MM_PER_INCH).toFixed(2))} × ` +
      `${trimTrailing((heightMm / MM_PER_INCH).toFixed(2))} in`
    );
  }
  return (
    `${trimTrailing(widthMm.toFixed(1))} × ` +
    `${trimTrailing(heightMm.toFixed(1))} mm`
  );
}

function trimTrailing(s: string): string {
  if (!s.includes(".")) return s;
  return s.replace(/\.?0+$/, "");
}
